import { YEAR_END_MAX_DATE, YEAR_END_MIN_DATE } from '@/shared/constants';
import { dateColumn } from '@/shared/grids/columns/dateColumn';
import { idColumn } from '@/shared/grids/columns/idColumn';
import { organisationLinkColumn } from '@/shared/grids/columns/organisationLinkColumn';
import { registrationNumberColumn } from '@/shared/grids/columns/registrationNumberColumn';
import { textColumn } from '@/shared/grids/columns/textColumn';
import { routes } from '@/shared/routes';
import {
  ClientType,
  DocumentLocation,
  OrganisationType,
  Priority,
  RelationshipType,
  RequestStatus,
  RequestType,
  TaskStatus,
  TaskType,
} from '@/shared/types/enum';
import { GridSettings } from '@/shared/types/grid';
import { enumOptions } from '@/shared/utils/enum';
import { formatEnum, formatFileSize } from '@/shared/utils/format';

import { DocumentView } from '@prisma/client';

export type DocumentGridRow = DocumentView;

const documentDownloadUrl = (location: string, parentId: string, fileKey: string) =>
  routes.documents.download({ location, parentId, key: fileKey });

export const documentGrid: GridSettings<DocumentGridRow> = {
  extraSelect: ['parentId', 'fileKey', 'clientType', 'taskId'],
  defaultSort: {
    addedOn: 'desc',
  },
  columns: [
    idColumn(),
    {
      uid: 'download',
      field: 'contentType',
      headerText: '',
      width: 40,
      allowFiltering: false,
      allowSorting: false,
      allowReordering: false,
      allowResizing: false,
      showInColumnChooser: false,
      template: (data: DocumentGridRow) =>
        data.location && data.parentId && data.fileKey
          ? `<a href="${documentDownloadUrl(data.location, data.parentId, data.fileKey)}" class="grid-link" title="Download" onclick="event.stopPropagation();"><i class="fa-solid fa-download"></i></a>`
          : '',
    },
    textColumn({
      header: 'Document Name',
      field: 'documentName',
      width: 260,
    }),
    textColumn({
      header: 'Client',
      field: 'clientId',
      width: 200,
      extra: {
        displayField: 'clientName',
        sortField: 'clientName',
        filter: {
          lookup: 'ClientView',
        },
      },
      template: (data: DocumentGridRow) => {
        if (!data.clientId || !data.clientName) {
          return '<span class="text-more-muted">Not set</span>';
        }
        const path =
          data.clientType === ClientType.Individual
            ? routes.individuals.edit({ id: data.clientId })
            : routes.organisations.edit({ id: data.clientId });
        return `<a href="${path}" class="grid-link" data-offcanvas="true">${data.clientName}</a>`;
      },
    }),
    textColumn({
      header: 'Client Reference',
      field: 'clientInternalReference',
      visible: false,
    }),
    textColumn({
      header: 'Client Type',
      field: 'clientTypeName',
      visible: false,
      extra: {
        filter: {
          options: [
            { text: 'Organisation', value: 'Organisation' },
            { text: 'Individual', value: 'Individual' },
          ],
        },
      },
    }),
    textColumn({
      header: 'Relationship',
      field: 'relationshipType',
      visible: false,
      template: (data) => formatEnum(RelationshipType, data.relationshipType),
      extra: {
        filter: {
          options: enumOptions(RelationshipType),
        },
      },
    }),
    textColumn({
      header: 'Account Manager',
      field: 'accountManagerId',
      visible: false,
      extra: {
        displayField: 'accountManagerName',
        sortField: 'accountManagerName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    textColumn({
      header: 'Partner',
      field: 'partnerId',
      visible: false,
      extra: {
        displayField: 'partnerName',
        sortField: 'partnerName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    {
      ...organisationLinkColumn({
        header: 'Group',
        idField: 'groupCompanyId',
        nameField: 'groupCompanyName',
        width: 180,
        extra: {
          sortField: 'groupCompanyName',
          filter: {
            lookup: 'Organisation',
          },
        },
      }),
      visible: false,
    },
    textColumn({
      header: 'Organisation Type',
      field: 'organisationType',
      visible: false,
      template: (data) => formatEnum(OrganisationType, data.organisationType),
      extra: {
        filter: {
          options: enumOptions(OrganisationType),
        },
      },
    }),
    {
      ...registrationNumberColumn({
        field: 'registrationNumber',
      }),
      visible: false,
    },
    textColumn({
      header: 'UTR',
      field: 'utr',
      visible: false,
    }),
    dateColumn({
      header: 'Year End',
      field: 'yearEndDate',
      format: 'dayMonth',
      visible: false,
      extra: {
        filter: {
          minDate: YEAR_END_MIN_DATE,
          maxDate: YEAR_END_MAX_DATE,
        },
      },
    }),
    textColumn({
      header: 'Location',
      field: 'location',
      width: 140,
      template: (data) => formatEnum(DocumentLocation, data.location),
      extra: {
        filter: {
          options: enumOptions(DocumentLocation),
        },
      },
    }),
    textColumn({
      header: 'Request Type',
      field: 'requestType',
      visible: false,
      template: (data) => formatEnum(RequestType, data.requestType),
      extra: {
        filter: {
          options: [
            { text: 'Upload', value: RequestType.Upload },
            { text: 'Download', value: RequestType.Download },
          ],
        },
      },
    }),
    textColumn({
      header: 'Task',
      field: 'task',
      width: 200,
      template: (data: DocumentGridRow) => {
        if (!data.taskId || !data.task) {
          return '<span class="text-more-muted">Not set</span>';
        }
        return `<a href="${routes.tasks.edit({ id: data.taskId })}" class="grid-link" data-offcanvas="true">${data.task}</a>`;
      },
    }),
    textColumn({
      header: 'Task Type',
      field: 'taskType',
      visible: false,
      template: (data) => formatEnum(TaskType, data.taskType),
      extra: {
        filter: {
          options: enumOptions(TaskType),
        },
      },
    }),
    textColumn({
      header: 'Service',
      field: 'serviceId',
      width: 140,
      extra: {
        displayField: 'serviceName',
        sortField: 'serviceName',
        filter: {
          lookup: 'Service',
        },
      },
    }),
    textColumn({
      header: 'Period',
      field: 'period',
      width: 140,
    }),
    textColumn({
      header: 'Task Status',
      field: 'taskStatus',
      visible: false,
      template: (data) => formatEnum(TaskStatus, data.taskStatus),
      extra: {
        filter: {
          options: enumOptions(TaskStatus),
        },
      },
    }),
    dateColumn({
      header: 'Deadline',
      field: 'deadlineDate',
      visible: false,
    }),
    textColumn({
      header: 'Assigned To',
      field: 'assignedToUserId',
      visible: false,
      extra: {
        displayField: 'assignedToUserName',
        sortField: 'assignedToUserName',
        filter: {
          lookup: 'MemberView',
        },
      },
    }),
    textColumn({
      header: 'Priority',
      field: 'priority',
      visible: false,
      template: (data) => formatEnum(Priority, data.priority),
      extra: {
        filter: {
          options: enumOptions(Priority),
        },
      },
    }),
    textColumn({
      header: 'File Type',
      field: 'fileType',
      width: 90,
    }),
    {
      uid: 'size',
      field: 'size',
      headerText: 'Size',
      type: 'number',
      width: 100,
      allowFiltering: true,
      allowSorting: true,
      template: (data: DocumentGridRow) =>
        data.size != null ? formatFileSize(Number(data.size)) : '<span class="text-more-muted">Not set</span>',
    },
    dateColumn({
      header: 'Added On',
      field: 'addedOn',
      format: 'dayMonthYearTime',
      width: 150,
    }),
    textColumn({
      header: 'Portal Request',
      field: 'portalRequestTitle',
      visible: false,
    }),
    textColumn({
      header: 'Portal Request Status',
      field: 'portalRequestStatus',
      visible: false,
      extra: {
        filter: {
          options: enumOptions({
            Draft: RequestStatus.Draft,
            Pending: RequestStatus.Pending,
            PendingApproval: RequestStatus.PendingApproval,
            Completed: RequestStatus.Completed,
            Approved: RequestStatus.Approved,
            Rejected: RequestStatus.Rejected,
          }),
        },
      },
    }),
    dateColumn({
      header: 'Portal Requested On',
      field: 'portalRequestedOn',
      format: 'dayMonthYearTime',
      visible: false,
    }),
    dateColumn({
      header: 'Portal Completed On',
      field: 'portalCompletedOn',
      format: 'dayMonthYearTime',
      visible: false,
    }),
  ],
};
